import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Link, Stack, Typography } from '@mui/material';

import { ListCard } from 'components/Card';
import { useAuth } from 'lib/auth';
import { isoToDateString } from 'utils/dateFormatter';
import { getApplicationLinks } from '../../utils/getApplicationLinks';
import { ApplicationStatusChip } from './ApplicationStatusChip';

const Column = ({ label, flex = 2, children }) => (
  <Stack flex={flex} justifyContent="center" sx={{ pr: 2, minWidth: 0 }}>
    <Typography variant="caption">{label}</Typography>
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }} component="div">
      {children || '-'}
    </Typography>
  </Stack>
);

const ColumnLink = ({ to, children }) =>
  to ? (
    <Link component={RouterLink} to={to} onClick={(event) => event.stopPropagation()} fontWeight={600}>
      {children}
    </Link>
  ) : (
    children
  );

export const ApplicationListItem = ({ application, showJobAd = true }) => {
  const { account, user } = useAuth();
  const navigate = useNavigate();
  const {
    ad_headline,
    first_name,
    last_name,
    price_per_hour,
    company_name,
    buyer_name,
    contact_user_name,
    created_on,
    status,
  } = application;

  const links = getApplicationLinks(application, { account, user });
  const openDetails = () => navigate(links.details);

  return (
    <ListCard
      role="button"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') openDetails();
      }}
      sx={{ cursor: 'pointer' }}
    >
      <Column label="Name" flex={2.5}>
        {`${first_name} ${last_name}`}
      </Column>
      {showJobAd && (
        <Column label="Job ad" flex={3}>
          <ColumnLink to={links.jobAd}>{ad_headline}</ColumnLink>
        </Column>
      )}
      {links.isApplicant ? (
        <Column label="Posted by">
          <ColumnLink to={links.postedBy.url}>{links.postedBy.name}</ColumnLink>
        </Column>
      ) : (
        <Column label="Supplier">
          <ColumnLink to={links.supplier}>{company_name}</ColumnLink>
        </Column>
      )}
      {account.super_admin && (
        <Column label="Buyer">
          <ColumnLink to={links.buyer}>{buyer_name}</ColumnLink>
        </Column>
      )}
      <Column label="Price per hour" flex={1.5}>
        {`${price_per_hour} SEK/h`}
      </Column>
      <Column label="Contact">
        <ColumnLink to={links.contact}>{contact_user_name}</ColumnLink>
      </Column>
      <Column label={links.isApplicant ? 'Date sent' : 'Date received'} flex={1.5}>
        {isoToDateString(created_on, true)}
      </Column>
      <Column label="Status" flex={1.5}>
        <ApplicationStatusChip status={status} />
      </Column>
    </ListCard>
  );
};
